import React, { useState } from 'react';
import { HiCheckCircle, HiChevronRight, HiEye } from './Icons';
import { PendingPaymentApprovalGroup, ReconciliationMatch } from '../services/api';

export type SidebarPendingCard = PendingPaymentApprovalGroup &
  Partial<ReconciliationMatch & { _ledgerId?: number }> & {
    paymentId: number;
    billId: number;
    amount: number;
    billAmount: number;
    flatNumber: string;
    blockName: string | null;
    transactionId: string | null;
    paymentMode: string | null;
    screenshotUrl?: string | null;
  };

export function groupToSidebarCard(g: PendingPaymentApprovalGroup): SidebarPendingCard {
  const paymentId = g.payment_ids[0]!;
  return {
    ...g,
    paymentId,
    billId: paymentId,
    amount: g.total_amount_paid,
    billAmount: g.total_amount_paid,
    flatNumber: g.flat_number || g.flat_label,
    blockName: g.block_name,
    transactionId: g.transaction_id,
    paymentMode: g.payment_mode,
  };
}

export function mergeGroupWithMatch(
  g: PendingPaymentApprovalGroup,
  match: ReconciliationMatch & { _ledgerId: number },
): SidebarPendingCard {
  const base = groupToSidebarCard(g);
  return {
    ...base,
    ...match,
    billAmount: match.billAmount ?? base.billAmount,
    amount: match.billAmount ?? base.amount,
    flatNumber: match.flatNumber || base.flatNumber,
    blockName: match.blockName ?? base.blockName,
  };
}

export type ReconPendingRow = ReturnType<typeof groupToReconRow>;

export function flatLabelFromCard(card: {
  block_name?: string | null;
  blockName?: string | null;
  flat_number?: string | null;
  flatNumber?: string | null;
  flat_label?: string;
}): string {
  const block = card.block_name ?? card.blockName;
  const flat = card.flat_number ?? card.flatNumber;
  if (block && flat) return `${block}-${flat}`;
  if (flat) return flat;
  return card.flat_label || 'Flat';
}

export function groupCardsByFlat<T extends { flat_id: number }>(
  cards: T[],
  labelFor: (card: T) => string,
): Array<{ flatId: number; flatLabel: string; cards: T[] }> {
  const map = new Map<number, T[]>();
  for (const card of cards) {
    const list = map.get(card.flat_id) ?? [];
    list.push(card);
    map.set(card.flat_id, list);
  }
  return Array.from(map.entries())
    .map(([flatId, flatCards]) => ({
      flatId,
      flatLabel: labelFor(flatCards[0]!),
      cards: flatCards,
    }))
    .sort((a, b) => a.flatLabel.localeCompare(b.flatLabel));
}

export function groupToReconRow(g: PendingPaymentApprovalGroup) {
  const card = groupToSidebarCard(g);
  const primarySubmission = g.pending_submissions?.[0];
  const amount =
    g.payment_ids.length === 1 && primarySubmission
      ? primarySubmission.amount
      : g.total_amount_paid;
  return {
    group_key: g.group_key,
    flat_id: g.flat_id,
    flatId: g.flat_id,
    paymentId: card.paymentId,
    paymentIds: g.payment_ids,
    amount,
    transactionId: g.transaction_id,
    flatNumber: card.flatNumber,
    blockName: g.block_name,
    billType: g.categories[0]?.bill_type || 'maintenance',
    paymentMode: g.payment_mode,
    screenshotFilePath: g.screenshot_file_path,
    residentName: null as string | null,
    billingDate: g.paid_at,
    paidAt: g.paid_at,
    remarks: g.remarks,
  };
}

type Props = {
  card: SidebarPendingCard;
  fmt: (n: number) => string;
  isMatched?: boolean;
  isSuspicious?: boolean;
  isHighRisk?: boolean;
  isUnmatched?: boolean;
  approved: boolean;
  approving: boolean;
  onViewDetails: () => void;
  onApprove?: () => void;
  onReject?: () => void;
};

const SocietyAdminReconcilePendingCard: React.FC<Props> = ({
  card,
  fmt,
  isMatched,
  isSuspicious,
  isHighRisk,
  isUnmatched,
  approved,
  approving,
  onViewDetails,
  onApprove,
  onReject,
}) => {
  const [expanded, setExpanded] = useState(false);
  const submissionCount = card.pending_submission_count ?? card.payment_ids.length;
  const showSubmissions = submissionCount > 1 && (card.pending_submissions?.length ?? 0) > 0;

  return (
    <div
      className={`reconcile-pending-card ${expanded ? 'is-expanded' : 'is-collapsed'} ${isMatched ? 'is-matched' : ''} ${isSuspicious ? 'is-suspicious' : ''} ${isHighRisk ? 'is-high-risk' : ''} ${isUnmatched ? 'is-unmatched' : ''}`}
    >
      <button
        type="button"
        className="reconcile-pending-header reconcile-pending-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse payment details' : 'Expand payment details'}
      >
        <div className="reconcile-pending-header-main">
          <div className="reconcile-pending-flat">
            {card.blockName ? `${card.blockName}-` : ''}
            {card.flatNumber}
          </div>
          <div className="reconcile-pending-meta">
            {submissionCount > 1
              ? `${submissionCount} categories · `
              : ''}
            Paid {fmt(card.total_amount_paid)}
            {card.payment_ids.length === 1 && (
              <span className="reconcile-payment-id"> · #{card.payment_ids[0]}</span>
            )}
            {card.transaction_id ? ` · Ref ${card.transaction_id}` : ''}
          </div>
        </div>
        <div className="reconcile-pending-header-end">
          <div className="reconcile-pending-outstanding">
            <span>Outstanding</span>
            <strong>{fmt(card.flat_summary.total_outstanding)}</strong>
          </div>
          <HiChevronRight
            size={18}
            className={`reconcile-pending-chevron ${expanded ? 'is-open' : ''}`}
            aria-hidden
          />
        </div>
      </button>

      <div
        className={`reconcile-bank-match-pill ${card.bank_matched ? 'matched' : 'unmatched'}`}
      >
        {card.bank_matched
          ? `Linked to bank line #${card.bank_summary_id}`
          : 'Not linked to bank statement yet'}
      </div>

      <div className="reconcile-received-block">
        <h4>Payment received</h4>
        <div className="reconcile-verify-grid">
          <div>
            <span className="recon-field-label">Amount paid</span>
            <strong>{fmt(card.total_amount_paid)}</strong>
          </div>
          <div>
            <span className="recon-field-label">Mode</span>
            <strong>{card.payment_mode || '—'}</strong>
          </div>
          <div>
            <span className="recon-field-label">Transaction ref</span>
            <strong className="recon-mono">{card.transaction_id || '—'}</strong>
          </div>
          <div>
            <span className="recon-field-label">Submitted</span>
            <strong>{new Date(card.paid_at).toLocaleString()}</strong>
          </div>
        </div>
        {card.screenshotUrl && (
          <a
            href={card.screenshotUrl}
            target="_blank"
            rel="noreferrer"
            className="reconcile-screenshot-link"
          >
            View payment screenshot
          </a>
        )}
      </div>

      {expanded && (
        <div className="reconcile-pending-expandable">
          <div className="reconcile-outstanding-block">
            <h4>Flat outstanding (before approval)</h4>
            <p className="reconcile-summary-line">
              Total due <strong>{fmt(card.flat_summary.total_outstanding)}</strong>
              {' '}(principal {fmt(card.flat_summary.total_principal)}
              {' · '}interest {fmt(card.flat_summary.total_interest)}
              {card.flat_summary.total_advance > 0
                ? ` · advance ${fmt(card.flat_summary.total_advance)}`
                : ''}
              )
            </p>
            {card.categories.length > 0 && (
              <table className="reconcile-mini-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {card.categories.slice(0, 5).map((c) => (
                    <tr key={c.bill_type}>
                      <td>{c.category_display_name || c.bill_type}</td>
                      <td>{fmt(c.total_due)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {card.categories.length > 5 && (
              <p className="reconcile-more-cats">+{card.categories.length - 5} more categories</p>
            )}
          </div>

          {showSubmissions && (
            <div className="reconcile-submissions-block">
              <h4>Pending transactions ({submissionCount})</h4>
              <table className="reconcile-mini-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>When</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {(card.pending_submissions ?? []).map((sub, index) => (
                    <tr key={sub.payment_id}>
                      <td>{index + 1}</td>
                      <td className="recon-mono">{new Date(sub.paid_at).toLocaleDateString()}</td>
                      <td>{sub.category_display_name}</td>
                      <td><strong>{fmt(sub.amount)}</strong></td>
                      <td className="recon-mono">{sub.transaction_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {card.payment_by_category.length > 0 && (
            <div className="reconcile-allocation-block">
              <h4>Allocation preview</h4>
              <table className="reconcile-mini-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Paid</th>
                    <th>Principal</th>
                    <th>Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {card.payment_by_category.map((slice) => (
                    <tr key={slice.payment_id}>
                      <td>{slice.category_display_name}</td>
                      <td><strong>{fmt(slice.amount_paid)}</strong></td>
                      <td>{fmt(slice.principal_applied)}</td>
                      <td>{fmt(slice.interest_applied)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(isMatched || isSuspicious) && card.bankDate && (
            <div className="sidebar-bank-info">
              <span>Bank {card.bankDate}</span>
              <span className="sidebar-bank-amount">Credited {fmt(card.bankAmount || 0)}</span>
            </div>
          )}

          {card.matchReason && (isMatched || isHighRisk || isSuspicious) && (
            <div className={`sidebar-match-reason ${isHighRisk ? 'high-risk' : ''}`}>
              {card.matchReason}
            </div>
          )}

          <div className="reconcile-pending-actions">
            <button type="button" className="recon-btn recon-btn-outline recon-btn-sm" onClick={onViewDetails}>
              <HiEye size={14} /> Details
            </button>
            {(isMatched || isSuspicious || isHighRisk) && onApprove && !approved && (
              <button
                type="button"
                className={`sidebar-approve-btn ${isMatched ? 'success' : isHighRisk ? 'danger' : 'warning'}`}
                onClick={onApprove}
                disabled={approving}
              >
                <HiCheckCircle size={14} /> {approving ? '…' : isMatched ? 'Approve' : 'Review'}
              </button>
            )}
            {(isHighRisk || isSuspicious) && onReject && (
              <button type="button" className="sidebar-reject-btn" onClick={onReject}>
                ✕ Reject
              </button>
            )}
          </div>
        </div>
      )}

      {!expanded && (
        <button
          type="button"
          className="reconcile-pending-more-btn"
          onClick={() => setExpanded(true)}
        >
          Show outstanding & allocation
        </button>
      )}
    </div>
  );
};

export default SocietyAdminReconcilePendingCard;
